import { createSSRApp, createApp as createStaticApp, defineComponent, h } from 'vue';
import { routerPlugin, routerStateKey } from './plugins/router';
import { appIdPlugin } from './plugins/app-id';
import { getServerState } from './state';
import { CapoPlugin, createHead } from '@unhead/vue';
import { InferSeoMetaPlugin } from '@unhead/addons';
import { getGlobalHooks } from './setup-hooks';
import ErrorBoundary from './components/ErrorBoundary';
/** @internal */
export async function createApp(appId) {
    const route = getServerState(routerStateKey, appId);
    if (!route)
        throw new Error('Routing information not found in context');
    // We need to map the component file's path to a dynamic import function
    // created by vite, because in dev mode this dynamic import will point at the
    // source file, and when built it'll point at the built js version of that
    // file. In other words if we try to import(route.pageFilePath) directly
    // vite's static import analysis will break and therefore the .vue file
    // wont get built by vue
    const vueFiles = import.meta.glob([
        '/node_modules/@ocp-zmarta/zsc-plugin-vite-ssr/vue/pages/**/*.vue',
        '/{vue/,lib/,src/,}pages/**/*.vue',
    ]);
    const component = (await vueFiles[route.pageFilePath]()).default;
    const wrappedComponent = defineComponent({
        render() { return h(ErrorBoundary, () => h(component)); },
    });
    const app = import.meta.env.MODE === 'static'
        ? createStaticApp(wrappedComponent)
        : createSSRApp(wrappedComponent);
    app.use(appIdPlugin(appId));
    app.use(routerPlugin);
    const head = createHead({
        plugins: [
            InferSeoMetaPlugin(),
        ],
    });
    if (import.meta.env.SSR) {
        head.use(CapoPlugin({ track: false }));
    }
    app.use(head);
    const globalHooks = await getGlobalHooks();
    await app.runWithContext(async () => {
        if (globalHooks.setupVue) {
            await globalHooks.setupVue({ app });
        }
    });
    return { app, head };
}
//# sourceMappingURL=main.js.map